/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { translate } from '@utils/i18n';
import { Modal } from '@components/v2/Modal';
import { Button } from '@ui';
import SectionGroup from '../SectionGroup';
import ManagerNote from '../IssueCard/ManagerNote';
import useIIMIssues from '../../hooks/useIIMIssues';
import useSendCorrections from '../../hooks/useSendCorrections';
import { IssueStatusEnum } from '../../types';
import './styles.scss';
/**
 * Modal component to display accepted issues with a general manager note
 */
export default function ReviewIssuesModal({ inspectionId, isOpen, onClose, modeIA }) {
    var _a, _b;
    const [generalManagerNote, setGeneralManagerNote] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    // Fetch issues marked for suggestion instead of already suggested ones
    const statuses = Object.values(IssueStatusEnum);
    const { data, isLoading } = useIIMIssues(inspectionId, statuses, modeIA, { markedForSuggestion: true });
    const sendCorrectionsMutation = useSendCorrections(inspectionId, (_b = (_a = data === null || data === void 0 ? void 0 : data.issues[0]) === null || _a === void 0 ? void 0 : _a.inspectionManagerRunId) !== null && _b !== void 0 ? _b : -1);
    // Handle sending corrections with general manager note
    const handleSave = () => {
        const markedIssues = (data === null || data === void 0 ? void 0 : data.issues) || [];
        if (markedIssues.length === 0) {
            toast.error('No issues marked for correction');
            return;
        }
        setIsSaving(true);
        sendCorrectionsMutation.mutate({ generalManagerNote, issuesCount: markedIssues.length }, {
            onSuccess: () => {
                toast.success('Corrections sent successfully');
                setIsSaving(false);
                onClose();
            },
            onError: () => {
                toast.error('Error sending corrections');
                setIsSaving(false);
            }
        });
    };
    const acceptedIssues = (data === null || data === void 0 ? void 0 : data.issues) || [];
    /**
     * Render the loading state
     */
    function renderLoading() {
        return React.createElement("div", { className: "review-issues-loading" }, translate('loading', { namespace: 'features.commons' }));
    }
    /**
     * Render the empty state when no accepted issues are found
     */
    function renderEmpty() {
        return (React.createElement("div", { className: "review-issues-empty" }, translate('no_accepted_issues', { namespace: 'features.iim_review.accepted_issues' })));
    }
    /**
     * Render the content with accepted issues and manager note
     */
    function renderContent() {
        return (React.createElement("div", { className: "review-issues-content" },
            React.createElement("p", { className: "review-issues-description" }, translate('accepted_issues_description', {
                namespace: 'features.iim_review.accepted_issues',
                count: acceptedIssues.length
            })),
            React.createElement("div", { className: "issues-list__content" }, data === null || data === void 0 ? void 0 : data.groupedIssues.map((group) => (React.createElement(SectionGroup, { key: group.title, group: group, inspectionId: inspectionId, readOnly: true })))),
            React.createElement("div", { className: "review-issues-general-note" },
                React.createElement(ManagerNote, { title: translate('overall_note_heading', { namespace: 'features.iim_review.review_issues_modal' }), note: generalManagerNote, onNoteChange: (note) => setGeneralManagerNote(note) }))));
    }
    /**
     * Determine which content to render based on loading state and data
     */
    function renderModalContent() {
        if (isLoading) {
            return renderLoading();
        }
        if (acceptedIssues.length === 0) {
            return renderEmpty();
        }
        return renderContent();
    }
    return (React.createElement(Modal, { title: translate('send_for_correction', { namespace: 'features.iim_review.accepted_issues' }), isOpen: isOpen, onClose: onClose, className: "review-issues-modal", width: "960px", footer: React.createElement(React.Fragment, null,
            React.createElement(Button, { onClick: onClose, disabled: isSaving }, translate('cancel', { namespace: 'features.commons' })),
            React.createElement(Button, { variant: "primary", onClick: () => {
                    void handleSave();
                }, disabled: isSaving }, isSaving
                ? translate('sending', { namespace: 'features.commons', defaultValue: 'Sending...' })
                : translate('send_for_correction', { namespace: 'features.iim_review.accepted_issues' }))) }, renderModalContent()));
}
